import { Box, Container, Flex, FlexProps, HStack } from '@chakra-ui/react'
import { useTranslation } from 'react-i18next'
import { FaPlus } from 'react-icons/fa'
import { LuSettings } from 'react-icons/lu'

import { useAuth } from '@/auth/useAuth'
import { SearchBar } from '@/components/molecules/Menu/SearchBar'
import { Button } from '@/components/ui/button'
import { useColorModeValue } from '@/components/ui/color-mode'
import { useTracking } from '@/hooks/useTracking'
import {
  AdminRoutes,
  PoolingRoutes,
  StaticPagesRoutes,
  UserRoutes,
} from '@/routes/_hooks/routes.enum'
import { Link } from '@/routing'

import { Logo } from '../../atoms/Logo/Logo'
import { NotificationsPopover } from '../Notifications/NotificationsPopover'
import { UserMenu } from './UserMenu'

export const HeadBar = ({ ...rest }: FlexProps) => {
  const { t } = useTranslation()
  const { user, token } = useAuth()
  const { captureEvent } = useTracking()

  return (
    <>
      <Flex
        as="header"
        px={{ base: 4, md: 4 }}
        height="20"
        zIndex={1000}
        alignItems="center"
        bg={useColorModeValue('white', 'gray.900')}
        borderBottomWidth="1px"
        borderBottomColor={useColorModeValue('gray.200', 'gray.700')}
        justifyContent={{ base: 'space-between' }}
        position="fixed"
        top="0"
        width="100%"
        {...rest}
      >
        <Container maxW="container.xl">
          <HStack gap={{ base: '0', md: '6' }} justifyContent="space-between">
            {/* Mobile logged in layout: icon + searchbar */}
            {user ? (
              <HStack
                gap={3}
                flex={1}
                display={{ base: 'flex', md: 'none' }}
                w="full"
              >
                <Box flexShrink={0}>
                  <Link
                    to={StaticPagesRoutes.Home}
                    onClick={() =>
                      captureEvent('navigation_logo_clicked', {
                        destination: StaticPagesRoutes.Home,
                        user_authenticated: !!user,
                      })
                    }
                  >
                    <img
                      src="/icon.svg"
                      width="32px"
                      height="32px"
                      alt="Communo icon"
                    />
                  </Link>
                </Box>
                <Flex flex={1} minW={0}>
                  <SearchBar />
                </Flex>
                {token && (
                  <Box flexShrink={0}>
                    <NotificationsPopover />
                  </Box>
                )}
              </HStack>
            ) : null}
            {/* Desktop logo - hide on mobile when logged in, always show on desktop */}
            <Box display={{ base: user ? 'none' : 'block', md: 'block' }}>
              <Link
                to={StaticPagesRoutes.Home}
                onClick={() =>
                  captureEvent('navigation_logo_clicked', {
                    destination: StaticPagesRoutes.Home,
                    user_authenticated: !!user,
                  })
                }
              >
                <Logo width="165px" />
              </Link>
            </Box>

            {/* Desktop searchbar */}
            <Flex grow={1} display={{ base: 'none', md: 'block' }}>
              <SearchBar />
            </Flex>
            <Flex alignItems="center">
              {(user && (
                <Button
                  asChild
                  size="sm"
                  mr={4}
                  display={{ base: 'none', sm: 'flex' }}
                  bgColor="brand.solid"
                  color="brand.contrast"
                  flex={1}
                  fontWeight={'bold'}
                  hideBelow="md"
                  _hover={{
                    bgGradient: 'to-l',
                    gradientFrom: 'purple.400',
                    gradientTo: 'pink.400',
                    color: 'yellow',
                    transform: 'translateY(2px)',
                    boxShadow: 'lg',
                  }}
                >
                  <Link
                    to={PoolingRoutes.New}
                    onClick={() =>
                      captureEvent('navigation_add_material_clicked', {
                        destination: PoolingRoutes.New,
                        user_authenticated: !!user,
                        source: 'header',
                      })
                    }
                  >
                    <FaPlus />

                    {t('layout.head.cta.label')}
                  </Link>
                </Button>
              )) || (
                <Button
                  asChild
                  size="sm"
                  mr={4}
                  colorPalette="brand"
                  flex={1}
                  fontSize="sm"
                  boxShadow="0px 1px 25px -5px rgb(66 153 225 / 48%), 0 10px 10px -5px rgb(66 153 225 / 43%)"
                >
                  <Link
                    to={UserRoutes.Login}
                    onClick={() =>
                      captureEvent('navigation_login_clicked', {
                        destination: UserRoutes.Login,
                        user_authenticated: !!user,
                        source: 'header',
                      })
                    }
                  >
                    {t('menu.login')}
                  </Link>
                </Button>
              )}
              {(token && user && (
                <Flex
                  alignItems="center"
                  gap={2}
                  display={{ base: 'none', md: 'flex' }}
                >
                  <NotificationsPopover />
                  {user.roles?.includes('ROLE_ADMIN') && (
                    <Button
                      asChild
                      size="md"
                      variant="ghost"
                      aria-label={t('menu.admin')}
                    >
                      <Link
                        to={AdminRoutes.Home}
                        onClick={() =>
                          captureEvent('navigation_admin_clicked', {
                            destination: AdminRoutes.Home,
                            user_id: user?.id,
                            user_roles: user?.roles,
                            source: 'header',
                          })
                        }
                      >
                        <LuSettings />
                      </Link>
                    </Button>
                  )}
                  <UserMenu />
                </Flex>
              )) || (
                <Box display={{ base: 'none', sm: 'flex' }}>
                  <Link to={UserRoutes.Register}>
                    {t('layout.head.register')}
                  </Link>
                </Box>
              )}
            </Flex>
          </HStack>
        </Container>
      </Flex>
    </>
  )
}
